import { useState, useEffect, useLayoutEffect, useRef, forwardRef, useImperativeHandle } from 'react';
import {
  IconChevronLeft, IconChevronRight, IconBold, IconItalic, IconUnderline,
  IconZoomIn, IconZoomOut, IconRotate, IconMaximize, IconMinimize,
  IconAlignLeft, IconAlignCenter, IconAlignRight, IconChevronDown, IconCheck,
  IconRowInsertBottom, IconColumnInsertRight, IconRowRemove, IconColumnRemove,
  IconArrowBackUp, IconArrowForwardUp,
} from '@tabler/icons-react';
import { renderMathInDom } from '../../../utils/aiDocMath';
import { sanitizeRichText, sanitizeDocHtml, docHtmlCss, DOC_FONTS, docFontStack, paperPx, PAPER_SIZES, computeHtmlPageBreaks, DOC_PAGE_MARGIN_Y, resolveMargins } from '../../../utils/aiDocExport';

// Custom tooltip for the floating editor toolbars — appears on hover BELOW the control.
// pointer-events:none so it never intercepts the button's mousedown (which preserves focus).
function Tip({ label, children }) {
  return (
    <span className="relative inline-flex group/tip">
      {children}
      <span className="pointer-events-none absolute top-full left-1/2 mt-1.5 -translate-x-1/2 whitespace-nowrap rounded-md bg-zinc-900 dark:bg-zinc-700 text-white text-[11px] font-medium px-2 py-1 opacity-0 group-hover/tip:opacity-100 transition-opacity duration-150 z-[70] shadow-lg">
        {label}
      </span>
    </span>
  );
}

// Focus-preserving custom dropdown. A native <select> steals focus from the block being
// edited (its blur commits + closes the editor before the menu opens) and can't be
// styled. This menu's trigger + items preventDefault on mousedown, so a contentEditable
// keeps focus. Reused for paper size, per-block font and the global document font.
export function CustomSelect({ value, onChange, options, className = '', placeholder = '', menuAlign = 'left' }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);
  const current = options.find((o) => o.value === value);
  const pick = (v) => { onChange?.(v); setOpen(false); };
  return (
    <div className={`relative ${className}`} ref={ref}>
      <button
        type="button"
        onMouseDown={(e) => { e.preventDefault(); setOpen((o) => !o); }}
        className="w-full h-full inline-flex items-center justify-between gap-2 px-3 rounded-xl border border-zinc-200 dark:border-zinc-700 bg-white dark:bg-zinc-900 text-sm text-zinc-700 dark:text-zinc-200 hover:border-primary/40 focus:outline-none"
      >
        <span className="truncate" style={current?.style}>{current ? current.label : placeholder}</span>
        <IconChevronDown size={16} className="text-zinc-400 shrink-0" />
      </button>
      {open && (
        <div className={`absolute z-40 mt-1 ${menuAlign === 'right' ? 'right-0' : 'left-0'} min-w-full w-max max-w-[260px] rounded-xl border border-zinc-200 dark:border-zinc-700 bg-white dark:bg-zinc-900 shadow-xl py-1 max-h-72 overflow-auto`}>
          {options.map((o) => {
            const active = o.value === value;
            return (
              <button
                key={String(o.value)}
                type="button"
                onMouseDown={(e) => { e.preventDefault(); pick(o.value); }}
                className={`w-full flex items-center gap-2 text-left px-2.5 py-1.5 text-sm hover:bg-zinc-100 dark:hover:bg-zinc-800 ${active ? 'text-primary font-medium' : 'text-zinc-700 dark:text-zinc-200'}`}
              >
                <IconCheck size={14} className={`shrink-0 ${active ? 'opacity-100' : 'opacity-0'}`} />
                <span className="truncate" style={o.style}>{o.label}</span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

// Font picker built on CustomSelect ('' = default; each option previews its own face).
export function FontSelect({ value, onChange, isBn = false, className = '', menuAlign = 'left' }) {
  const options = [
    { value: '', label: isBn ? 'ডিফল্ট ফন্ট' : 'Default font' },
    // Font NAME stays English; each option is previewed in its OWN typeface (the style).
    ...DOC_FONTS.map((f) => ({ value: f.id, label: f.label, style: { fontFamily: f.stack } })),
  ];
  return (
    <CustomSelect
      value={value || ''}
      onChange={(v) => onChange?.(v || null)}
      options={options}
      className={className}
      menuAlign={menuAlign}
      placeholder={isBn ? 'ডিফল্ট ফন্ট' : 'Default font'}
    />
  );
}

// Inline document editor — renders the block model as fixed A4-size paper pages whose
// typography matches the PDF export (aiDocExport.js). Long documents split across
// pages (measured) with a page navigator. Editing:
//   • Click a highlighted [placeholder] → edit ONLY that variable inline.
//   • Click anywhere else in a block → rich-text edit the paragraph in place
//     (contentEditable) with Bold / Italic / Underline — buttons + Ctrl/Cmd+B/I/U.

const PH_RE = /(\[[^\]]+\])/;
const splitTokens = (text) => String(text || '').split(PH_RE);
const isPh = (tok) => /^\[[^\]]+\]$/.test(tok);
const EN_TO_BN = ['০', '১', '২', '৩', '৪', '৫', '৬', '৭', '৮', '৯'];
const toBn = (n) => String(n).replace(/[0-9]/g, (d) => EN_TO_BN[d]);

// Per-block font-size choices (px) for the inline toolbar; '' = the kind's default.
const SIZE_OPTS = [10, 11, 12, 13, 14, 15, 16, 18, 20, 22, 24, 28, 32, 36, 40, 48];

// Page padding @ 96dpi. Page dimensions come from the selected paper size. Landscape is
// much wider, so it gets a bigger horizontal margin (a visible left/right gap).

// Render a block's stored text as display HTML: sanitized inline formatting +
// clickable placeholder chips (data-i = placeholder ordinal within the block).
function richBlockHtml(text) {
  let n = -1;
  const html = sanitizeRichText(text, { breaks: '\n' });
  const out = html.replace(/\[([^\]]+)\]/g, (m) => {
    n += 1;
    return `<span class="adoc-ph" data-i="${n}" title="Click to fill">${m}</span>`;
  });
  return out || '&nbsp;';
}

const BlockEditor = forwardRef(function BlockEditor(
  { blocks = [], onChange, editable = true, lang = 'en', zoom = 1, onEditingChange, paper = 'a4', landscape = false, globalFont = null, fontScale = 1, margins = null },
  apiRef,
) {
  const isBn = lang === 'bn';
  const bnStack = isBn ? ",'Kalpurush','Noto Serif Bengali'" : '';
  const globalStack = docFontStack(globalFont); // document-wide font (per-block wins)
  const bodyFont = (globalStack || "Georgia,'Times New Roman',serif") + bnStack;
  const metaFont = "'SF Mono',Menlo,monospace" + bnStack;
  const headingFont = 'Arial,Helvetica,sans-serif' + bnStack;

  // Live page dimensions from the selected paper size + orientation + margin setting.
  const { w: PAGE_W, h: PAGE_H } = paperPx(paper, landscape);
  const M = resolveMargins(margins, { landscape, html: false });
  const CONTENT_W = Math.max(160, PAGE_W - M.left - M.right);
  const CONTENT_H = Math.max(120, PAGE_H - M.top - M.bottom);

  // Match buildPrintHtml() styles so the preview mirrors the PDF.
  const KIND_STYLE = {
    title: { fontSize: 27, fontWeight: 600, textAlign: 'center', textTransform: 'uppercase', letterSpacing: '0.03em', color: '#1a1a1a', margin: '0 0 2px' },
    meta: { fontFamily: metaFont, fontSize: 11, textAlign: 'center', letterSpacing: '0.12em', textTransform: 'uppercase', color: '#9a9a93', margin: '2px 0 26px' },
    heading: { fontFamily: headingFont, fontSize: 12.5, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em', color: '#1a1a1a', margin: '24px 0 2px' },
    para: { fontSize: 15.5, lineHeight: 1.75, whiteSpace: 'pre-line', color: '#2c2c29', margin: '0 0 4px' },
    sig: { fontSize: 15, lineHeight: 1.9, whiteSpace: 'pre-line', color: '#2c2c29', margin: '30px 0 0' },
  };

  const [editIdx, setEditIdx] = useState(-1);
  const [phEdit, setPhEdit] = useState(null); // { block, token }
  const [phDraft, setPhDraft] = useState('');

  // Per-block style: a per-block font override wins; otherwise the document-wide
  // globalFont applies (to para/sig only, so headings keep their distinct face).
  const blockStyle = (b) => {
    const base = KIND_STYLE[b.kind] || KIND_STYLE.para;
    const stack = docFontStack(b.font) || (globalStack && (b.kind === 'para' || b.kind === 'sig' || b.kind === 'title') ? globalStack : null);
    // Effective size = per-block override (or the kind default) × document-wide scale.
    const baseSize = (typeof b.fontSize === 'number' && b.fontSize > 0) ? b.fontSize : base.fontSize;
    return {
      ...base,
      fontSize: baseSize * (fontScale || 1),
      ...(b.align ? { textAlign: b.align } : {}),
      ...(stack ? { fontFamily: stack + bnStack } : {}),
    };
  };
  const [pages, setPages] = useState([blocks.map((_, i) => i)]);
  const [fontTick, setFontTick] = useState(0);
  const measureRefs = useRef([]);
  const measureBoxRef = useRef(null);
  const editRef = useRef(null);

  useEffect(() => {
    let alive = true;
    if (typeof document !== 'undefined' && document.fonts && document.fonts.ready) {
      document.fonts.ready.then(() => { if (alive) setFontTick((t) => t + 1); });
    }
    return () => { alive = false; };
  }, []);

  // Focus the rich editor (caret at end) when a block enters edit mode.
  useEffect(() => {
    const el = editRef.current;
    if (editIdx < 0 || !el) return;
    el.focus();
    try {
      const range = document.createRange();
      range.selectNodeContents(el);
      range.collapse(false);
      const sel = window.getSelection();
      sel.removeAllRanges();
      sel.addRange(range);
    } catch { /* caret placement is best-effort */ }
  }, [editIdx]);

  // Pack blocks into pages at BLOCK boundaries using the hidden layer's REAL geometry
  // (offsets include margins & collapse) — the same rule the PDF slicer and native print
  // use, so preview, PDF and print all break on exactly the same block.
  useLayoutEffect(() => {
    const box = measureBoxRef.current;
    const els = blocks.map((_, i) => measureRefs.current[i]);
    const totalH = box ? box.scrollHeight : 0;
    const out = [];
    let cur = [];
    let start = 0;
    for (let i = 0; i < blocks.length; i++) {
      const el = els[i];
      const top = el ? el.offsetTop : 0;
      const end = i + 1 < blocks.length && els[i + 1] ? els[i + 1].offsetTop : totalH;
      if (cur.length && top > start && end - start > CONTENT_H) { out.push(cur); cur = []; start = top; }
      cur.push(i);
    }
    if (cur.length) out.push(cur);
    if (!out.length) out.push([]);
    setPages(out);
  }, [blocks, lang, fontTick, paper, landscape, fontScale, globalFont, M.top, M.bottom, M.left, M.right]);

  // ── Whole-block rich edit ────────────────────────────────────────────────────
  const startBlock = (i) => {
    if (!editable) return;
    setPhEdit(null);
    setEditIdx(i);
  };
  const commitBlock = (i) => {
    const el = editRef.current;
    if (el && onChange) onChange(i, sanitizeRichText(el.innerHTML, { breaks: '\n' }));
    setEditIdx(-1);
  };
  const exec = (cmd) => {
    document.execCommand(cmd, false, null);
    editRef.current?.focus();
  };
  const setBlockMeta = (meta) => {
    if (editIdx >= 0 && onChange) onChange(editIdx, undefined, meta);
  };

  // Imperative API for external tool panels (the step-3 right sidebar).
  useImperativeHandle(apiRef, () => ({
    exec,
    setAlign: (a) => setBlockMeta({ align: a }),
    setFont: (f) => setBlockMeta({ font: f || null }),
    // Clear both overrides in ONE change (separate calls would clobber each other).
    resetMeta: () => setBlockMeta({ align: null, font: null }),
    editingIdx: editIdx,
  }), [editIdx, onChange]);

  useEffect(() => { onEditingChange?.(editIdx); }, [editIdx]);
  const onEditKeyDown = (e, i) => {
    const mod = e.metaKey || e.ctrlKey;
    if (mod && e.key.toLowerCase() === 'b') { e.preventDefault(); exec('bold'); }
    else if (mod && e.key.toLowerCase() === 'i') { e.preventDefault(); exec('italic'); }
    else if (mod && e.key.toLowerCase() === 'u') { e.preventDefault(); exec('underline'); }
    else if (mod && e.key === 'Enter') { e.preventDefault(); commitBlock(i); }
    else if (e.key === 'Escape') { e.preventDefault(); setEditIdx(-1); }
  };

  // ── Placeholder-only edit ────────────────────────────────────────────────────
  const startPh = (bi, ordinal) => {
    if (!editable) return;
    setEditIdx(-1);
    // Map placeholder ordinal → token index in the raw text.
    const tokens = splitTokens(blocks[bi]?.text);
    let n = -1;
    let tokenIdx = -1;
    for (let ti = 0; ti < tokens.length; ti++) {
      if (isPh(tokens[ti])) { n += 1; if (n === ordinal) { tokenIdx = ti; break; } }
    }
    if (tokenIdx < 0) return;
    setPhDraft('');
    setPhEdit({ block: bi, token: tokenIdx });
  };
  const commitPh = (bi, ti) => {
    const tokens = splitTokens(blocks[bi]?.text);
    if (phDraft.trim() && onChange) {
      tokens[ti] = phDraft;
      onChange(bi, tokens.join(''));
    }
    setPhEdit(null);
    setPhDraft('');
  };

  const onBlockClick = (e, i) => {
    const ph = e.target.closest?.('.adoc-ph');
    if (ph && editable) {
      e.stopPropagation();
      startPh(i, parseInt(ph.dataset.i, 10) || 0);
      return;
    }
    startBlock(i);
  };

  // `firstOnPage` — the first block of a page drops its own top margin, exactly like
  // the PDF's margin band masks it and native print fragmentation truncates it.
  const renderBlock = (b, i, firstOnPage = false) => {
    const topFix = firstOnPage ? { marginTop: 0 } : null;
    // Rich whole-block editing (contentEditable) with a floating B/I/U toolbar.
    if (editable && editIdx === i) {
      return (
        <div key={i} className="relative my-1">
          <div
            className="absolute -top-12 left-0 z-10 flex items-center gap-1 rounded-xl border border-zinc-200 bg-white px-1.5 py-1"
            style={{ boxShadow: '0 14px 36px rgba(0,0,0,0.22), 0 4px 12px rgba(0,0,0,0.12)' }}
          >
            {/* Font */}
            <FontSelect
              value={b.font || ''}
              onChange={(id) => setBlockMeta({ font: id })}
              isBn={isBn}
              className="h-8 w-[124px] text-xs"
            />
            {/* Font size (per block; '' = the kind's default) */}
            <CustomSelect
              value={b.fontSize || ''}
              onChange={(v) => setBlockMeta({ fontSize: v || null })}
              options={[{ value: '', label: isBn ? 'অটো' : 'Auto' }, ...SIZE_OPTS.map((n) => ({ value: n, label: isBn ? toBn(n) : String(n) }))]}
              className="h-8 w-[68px] text-xs"
            />
            <span className="w-px h-5 bg-zinc-200 mx-0.5" />
            {/* Bold / Italic / Underline */}
            {[
              { cmd: 'bold', Icon: IconBold, k: 'B' },
              { cmd: 'italic', Icon: IconItalic, k: 'I' },
              { cmd: 'underline', Icon: IconUnderline, k: 'U' },
            ].map(({ cmd, Icon, k }) => (
              <button
                key={cmd}
                type="button"
                title={`${cmd} (Ctrl+${k})`}
                onMouseDown={(e) => { e.preventDefault(); exec(cmd); }}
                className="w-8 h-8 rounded-lg flex items-center justify-center text-zinc-600 hover:bg-zinc-100 hover:text-zinc-900 transition-colors"
              >
                <Icon size={15} />
              </button>
            ))}
            <span className="w-px h-5 bg-zinc-200 mx-0.5" />
            {/* Alignment */}
            {[
              { a: 'left', Icon: IconAlignLeft },
              { a: 'center', Icon: IconAlignCenter },
              { a: 'right', Icon: IconAlignRight },
            ].map(({ a, Icon }) => (
              <button
                key={a}
                type="button"
                title={a}
                onMouseDown={(e) => { e.preventDefault(); setBlockMeta({ align: a }); }}
                className={`w-8 h-8 rounded-lg flex items-center justify-center transition-colors hover:bg-zinc-100 ${b.align === a ? 'text-primary bg-primary/10' : 'text-zinc-600 hover:text-zinc-900'}`}
              >
                <Icon size={15} />
              </button>
            ))}
            <span className="w-px h-5 bg-zinc-200 mx-0.5" />
            {/* Done · Esc — commit + exit edit mode */}
            <button
              type="button"
              title={isBn ? 'সম্পন্ন (Esc)' : 'Done (Esc)'}
              onMouseDown={(e) => { e.preventDefault(); commitBlock(i); }}
              className="inline-flex items-center gap-1 h-8 px-2.5 rounded-lg text-xs font-semibold text-zinc-700 hover:bg-zinc-100 transition-colors whitespace-nowrap"
            >
              <IconCheck size={14} className="text-primary" />
              {isBn ? 'শেষ' : 'Done'}<span className="font-normal text-zinc-400"> · Esc</span>
            </button>
          </div>
          <div
            ref={(el) => { editRef.current = el; }}
            contentEditable
            suppressContentEditableWarning
            onBlur={() => commitBlock(i)}
            onKeyDown={(e) => onEditKeyDown(e, i)}
            style={{ ...blockStyle(b), whiteSpace: 'pre-wrap', margin: 0 }}
            className="w-full rounded-md border border-primary/60 bg-primary/5 px-2 py-1 focus:outline-none focus:ring-2 focus:ring-primary/40"
            dangerouslySetInnerHTML={{ __html: sanitizeRichText(b.text, { breaks: '<br/>' }) || '&nbsp;' }}
          />
        </div>
      );
    }

    // Placeholder-edit mode: render tokens as plain text with an inline input at the
    // active token (formatting hidden just for this block while editing the variable).
    if (editable && phEdit && phEdit.block === i) {
      return (
        <div key={i} className="adoc-block" style={{ ...blockStyle(b), ...topFix }}>
          {splitTokens(b.text).map((tok, ti) => {
            if (phEdit.token === ti) {
              const label = tok.slice(1, -1);
              return (
                <input
                  key={ti}
                  autoFocus
                  value={phDraft}
                  placeholder={label}
                  size={Math.max((phDraft || label).length + 1, 6)}
                  onClick={(e) => e.stopPropagation()}
                  onChange={(e) => setPhDraft(e.target.value)}
                  onBlur={() => commitPh(i, ti)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') { e.preventDefault(); commitPh(i, ti); }
                    if (e.key === 'Escape') { e.preventDefault(); setPhEdit(null); }
                  }}
                  className="adoc-ph-input"
                />
              );
            }
            return <span key={ti}>{tok.replace(/<[^>]+>/g, '')}</span>;
          })}
        </div>
      );
    }

    return (
      <div
        key={i}
        className={`adoc-block ${editable ? 'cursor-text' : ''}`}
        style={{ ...blockStyle(b), ...topFix }}
        onClick={(e) => editable && onBlockClick(e, i)}
        title={editable ? 'Click to edit' : undefined}
        dangerouslySetInnerHTML={{ __html: richBlockHtml(b.text) }}
      />
    );
  };

  const scale = Math.max(0.4, Math.min(2, zoom || 1));

  return (
    <div className="w-full flex flex-col items-center gap-4">
      {/* Hidden measuring layer — all blocks at the page's content width, REAL margins,
          so offsets mirror the visible pages and the PDF/print layout exactly. */}
      <div aria-hidden ref={measureBoxRef} style={{ position: 'fixed', left: -99999, top: 0, width: CONTENT_W, fontFamily: bodyFont, visibility: 'hidden', pointerEvents: 'none', zIndex: -1 }}>
        {blocks.map((b, i) => (
          <div
            key={i}
            ref={(el) => { measureRefs.current[i] = el; }}
            style={blockStyle(b)}
            dangerouslySetInnerHTML={{ __html: richBlockHtml(b.text) }}
          />
        ))}
      </div>

      <style>{`
        .adoc-ph{background:#fff3cd;color:#8a6d00;border-radius:3px;padding:0 3px;font-weight:500;cursor:pointer;}
        .adoc-ph:hover{outline:2px solid rgba(230,138,0,0.45);}
        .adoc-ph-input{background:#fff3cd;color:#8a6d00;border:1px solid #e6a700;border-radius:3px;padding:0 3px;font:inherit;outline:none;min-width:3rem;}
        .adoc-block:hover{background:rgba(230,138,0,0.05);border-radius:4px;}
      `}</style>

      {/* All pages stacked — scroll to read; each sheet breaks on the same blocks as the PDF/print. */}
      {pages.map((idxs, p) => (
        <div key={p} style={{ width: PAGE_W * scale, height: PAGE_H * scale, flexShrink: 0 }}>
          <div
            className="adoc-doc"
            style={{
              width: PAGE_W,
              height: PAGE_H,
              overflow: 'hidden',
              transform: `scale(${scale})`,
              transformOrigin: 'top left',
              background: '#fff',
              color: '#2c2c29',
              fontFamily: bodyFont,
              padding: `${M.top}px ${M.right}px ${M.bottom}px ${M.left}px`,
              borderRadius: 4,
              border: '1px solid #ece9e4',
              boxShadow: '0 1px 2px rgba(0,0,0,0.06), 0 14px 40px rgba(0,0,0,0.10)',
            }}
          >
            {idxs.map((i, k) => renderBlock(blocks[i], i, k === 0))}
          </div>
        </div>
      ))}
    </div>
  );
});

export default BlockEditor;

// ── Canvas-style preview shell ─────────────────────────────────────────────────
// "Live preview" toolbar (A4 label, zoom −/reset/+, %, fullscreen) over a dotted
// canvas background — the wrapper both the wizard's step 3 and the detail page use.
export function DocPreviewCanvas({ lang = 'en', name = '', paper = 'a4', landscape = false, children }) {
  const isBn = lang === 'bn';
  const { w: pw0, h: ph0 } = paperPx(paper, landscape);
  const paperObj = PAPER_SIZES.find((p) => p.id === paper) || PAPER_SIZES[0];
  const sizeLabel = `${paperObj.label} — ${pw0}×${ph0} px`;
  const [zoom, setZoom] = useState(1);
  const [isFull, setIsFull] = useState(false);
  const boxRef = useRef(null);

  useEffect(() => {
    const onFs = () => setIsFull(!!document.fullscreenElement);
    document.addEventListener('fullscreenchange', onFs);
    return () => document.removeEventListener('fullscreenchange', onFs);
  }, []);

  const toggleFull = () => {
    if (document.fullscreenElement) document.exitFullscreen?.();
    else boxRef.current?.requestFullscreen?.();
  };
  const pct = Math.round(zoom * 100);

  const zBtn = 'w-8 h-8 rounded-lg border border-zinc-200 dark:border-zinc-700 flex items-center justify-center text-zinc-600 dark:text-zinc-300 hover:border-primary/40 hover:text-primary transition-colors disabled:opacity-40';

  return (
    <div ref={boxRef} className="rounded-2xl border border-zinc-200 dark:border-zinc-700 overflow-hidden bg-white dark:bg-zinc-900 flex flex-col" style={isFull ? { height: '100%' } : undefined}>
      <div className="flex items-center gap-3 px-4 py-2.5 border-b border-zinc-200 dark:border-zinc-700 bg-white dark:bg-zinc-900">
        <p className="text-sm font-semibold text-zinc-900 dark:text-zinc-100 truncate max-w-[55%]">
          {name || (isBn ? 'ডকুমেন্ট' : 'Document')}
        </p>
        <span className="text-[11px] text-zinc-400 whitespace-nowrap shrink-0">{sizeLabel}</span>
        <div className="ml-auto flex items-center gap-1.5">
          <span className="text-xs text-zinc-500 w-10 text-right">{isBn ? toBn(pct) : pct}%</span>
          <Tip label={isBn ? 'ছোট করুন' : 'Zoom out'}>
            <button type="button" className={zBtn} onClick={() => setZoom((z) => Math.max(0.4, +(z - 0.1).toFixed(2)))} disabled={zoom <= 0.4}>
              <IconZoomOut size={16} />
            </button>
          </Tip>
          <Tip label={isBn ? 'জুম রিসেট' : 'Reset zoom'}>
            <button type="button" className={zBtn} onClick={() => setZoom(1)}>
              <IconRotate size={15} />
            </button>
          </Tip>
          <Tip label={isBn ? 'বড় করুন' : 'Zoom in'}>
            <button type="button" className={zBtn} onClick={() => setZoom((z) => Math.min(2, +(z + 0.1).toFixed(2)))} disabled={zoom >= 2}>
              <IconZoomIn size={16} />
            </button>
          </Tip>
          <Tip label={isFull ? (isBn ? 'ফুলস্ক্রিন বন্ধ' : 'Exit fullscreen') : (isBn ? 'ফুলস্ক্রিন' : 'Fullscreen')}>
            <button type="button" className={zBtn} onClick={toggleFull}>
              {isFull ? <IconMinimize size={16} /> : <IconMaximize size={16} />}
            </button>
          </Tip>
        </div>
      </div>
      <div
        className="flex-1 overflow-auto p-4 md:p-8"
        style={{
          background: '#fafafa',
          backgroundImage: 'radial-gradient(#d6d3ce 1px, transparent 1px)',
          backgroundSize: '16px 16px',
        }}
      >
        {typeof children === 'function' ? children(zoom) : children}
      </div>
    </div>
  );
}

// Inject DISPLAY-ONLY sheet separators into the live HTML editor so the whole document
// scrolls as stacked A4 pages (like BlockEditor) instead of a one-page + navigator view.
// Each spacer reserves the current page's bottom margin + a grey gap + the next page's top
// margin, and full-bleeds horizontally so the white sheet visually splits into pages. The
// `.adoc-pagegap` class is stripped by sanitizeDocHtml, so spacers never persist/export.
const ADOC_SHEET_GAP = 28;
// Align the flowing content to FIXED page-sheet positions: each page's content sits inside
// its own A4 sheet [i*(PH+gap) … +PH] (with a top margin), and a TRANSPARENT spacer bridges
// the sheet gap so the dotted canvas + the sheets' shadows show through — real separate
// pages, not one long sheet. Returns the page count so the caller can draw that many sheets.
function injectPageGaps(el, { PH, usableH, gap, top, padL, padR }) {
  if (!el) return 1;
  el.querySelectorAll('.adoc-pagegap').forEach((s) => s.remove());
  if (!(usableH > 0) || !(PH > 0)) return 1;
  let pageIndex = 0;
  let child = el.firstElementChild;
  let guard = 0;
  while (child && guard++ < 600) {
    const next = child.nextElementSibling;
    if (child.classList && child.classList.contains('adoc-pagegap')) { child = next; continue; }
    const pageContentTop = top + pageIndex * (PH + gap);
    const bottomY = child.offsetTop + child.offsetHeight;
    // This child overflows the current page's usable area → push it to the next sheet.
    if (child.offsetTop > pageContentTop + 1 && bottomY > pageContentTop + usableH + 1) {
      pageIndex += 1;
      const targetTop = top + pageIndex * (PH + gap);
      const spacerH = Math.max(0, Math.round(targetTop - child.offsetTop));
      if (spacerH > 0) {
        const sp = document.createElement('div');
        sp.className = 'adoc-pagegap';
        sp.setAttribute('contenteditable', 'false');
        sp.style.cssText = `height:${spacerH}px;margin:0 -${padR}px 0 -${padL}px;background:transparent;pointer-events:none;user-select:none;`;
        el.insertBefore(sp, child);
      }
    }
    child = next;
  }
  return pageIndex + 1;
}

// ── HTML-format document editor ────────────────────────────────────────────────
// For reference/form documents the AI returns a full HTML string (tables etc.),
// rendered in the SAME scaled paper as BlockEditor and edited as one contentEditable
// surface — so step-3 editing/export feel identical. The imperative API mirrors
// BlockEditor (exec / setAlign / setFont / resetMeta) via execCommand, plus getHtml()
// so export/save can read the live edited content.
// Wrap bare [Label] placeholders in yellow "fill-me" chips (display only — sanitizeDocHtml
// strips them back out before persist/export, so filling a placeholder clears the chip).
function markPlaceholders(root) {
  if (!root || typeof document === 'undefined') return;
  root.querySelectorAll('span.adoc-ph').forEach((s) => { s.replaceWith(document.createTextNode(s.textContent || '')); });
  const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT);
  const texts = [];
  while (walker.nextNode()) {
    const n = walker.currentNode;
    if (n.parentElement && n.parentElement.closest('.adoc-math')) continue; // a [..] inside a rendered formula is TeX, not a placeholder
    if (/\[[^\]]+\]/.test(n.nodeValue || '')) texts.push(n);
  }
  texts.forEach((node) => {
    const v = node.nodeValue;
    const frag = document.createDocumentFragment();
    let last = 0;
    const re = /\[[^\]]+\]/g;
    let mm;
    while ((mm = re.exec(v))) {
      if (mm.index > last) frag.appendChild(document.createTextNode(v.slice(last, mm.index)));
      const span = document.createElement('span');
      span.className = 'adoc-ph';
      // Atomic + non-editable so the caret can't land inside a [Label]; a click opens
      // the inline fill input (see HtmlDocEditor's chip handler) instead of typing over it.
      span.setAttribute('contenteditable', 'false');
      span.setAttribute('title', 'Click to fill');
      span.textContent = mm[0];
      frag.appendChild(span);
      last = mm.index + mm[0].length;
    }
    if (last < v.length) frag.appendChild(document.createTextNode(v.slice(last)));
    node.replaceWith(frag);
  });
}

// Display decoration for the editable surface: KaTeX-render formulas first ($...$ → non-editable
// span.adoc-math), then wrap [Label] placeholders (formulas are skipped). Both are display-only —
// sanitizeDocHtml turns them back into plain text before persist / export.
function decorate(root) {
  renderMathInDom(root);
  markPlaceholders(root);
}

export const HtmlDocEditor = forwardRef(function HtmlDocEditor(
  { html = '', onChange, editable = true, lang = 'en', zoom = 1, onEditingChange, paper = 'a4', landscape = false, globalFont = null, fontScale = 1, margins = null, preserveStyles = false },
  apiRef,
) {
  // Template-set docs keep their authored typography; sanitize with that flag everywhere.
  const sanitize = (h) => sanitizeDocHtml(h, { preserveStyles });
  const isBn = lang === 'bn';
  const bnStack = isBn ? ",'Kalpurush','Noto Serif Bengali'" : '';
  const globalStack = docFontStack(globalFont);
  const bodyFont = (globalStack || "Georgia,'Times New Roman',serif") + bnStack;
  const M = resolveMargins(margins, { landscape, html: true });
  const MY = M.top; // top margin reserved on EVERY page (bottom = M.bottom via USABLE_H)
  const { w: PAGE_W, h: PAGE_H } = paperPx(paper, landscape);
  const USABLE_H = Math.max(120, PAGE_H - M.top - M.bottom); // content height available per page
  const ref = useRef(null);
  const measureRef = useRef(null);
  const savedRange = useRef(null); // last selection inside the editor (survives toolbar clicks)
  const [sheetCount, setSheetCount] = useState(1); // number of A4 sheets to draw behind content
  const [focused, setFocused] = useState(false);
  const [bar, setBar] = useState(null); // floating inline toolbar { top, left }
  const [tableBar, setTableBar] = useState(null); // table add/remove row-col toolbar { top, left }
  const activeCell = useRef(null); // <td>/<th> holding the caret (drives the table toolbar)
  const [fmt, setFmt] = useState({}); // live formatting state of the caret/selection (B/I/U + align)
  const [fitFont, setFitFont] = useState(14 * (fontScale || 1)); // base font after fit-to-page
  // Page-start Y offsets (row-boundary breaks); length === page count. [0] = single page.
  const [pageBreaks, setPageBreaks] = useState([0]);
  const pages = pageBreaks.length;
  const [page, setPage] = useState(0);
  const [fontTick, setFontTick] = useState(0);
  // Inline "fill a [placeholder]" input, positioned over the clicked chip (mirrors the
  // block editor's variable-click-to-write). { top, left, height, font, label, chip }.
  const [phFill, setPhFill] = useState(null);
  const [phDraft, setPhDraft] = useState('');

  const baseFont = 14 * (fontScale || 1);

  // Push sanitized html into the contentEditable (only while NOT editing, so a parent
  // re-render never clobbers the caret) AND into the hidden measuring node.
  useEffect(() => {
    const clean = sanitize(html) || '<p><br/></p>';
    const el = ref.current;
    if (el && document.activeElement !== el) { el.innerHTML = clean; decorate(el); }
    if (measureRef.current) { measureRef.current.innerHTML = clean; decorate(measureRef.current); }
  }, [html]);

  // Web-fonts change text height — re-measure once they load.
  useEffect(() => {
    let alive = true;
    if (typeof document !== 'undefined' && document.fonts && document.fonts.ready) {
      document.fonts.ready.then(() => { if (alive) setFontTick((t) => t + 1); });
    }
    return () => { alive = false; };
  }, []);

  // Measure the content (full page width + same padding) → fit to ONE page by shrinking the
  // font when it's within ~1.5 pages; otherwise paginate. Mirrors the PDF/print fit exactly.
  useLayoutEffect(() => {
    const m = measureRef.current;
    if (!m) { setPageBreaks([0]); return; }
    // Ensure the hidden node reflects the CURRENT html (the innerHTML useEffect runs after
    // this layout effect), so break points are measured from up-to-date content.
    m.innerHTML = sanitize(html) || '<p><br/></p>';
    decorate(m);
    m.style.fontSize = `${baseFont}px`;
    let ff = baseFont;
    if (m.scrollHeight > USABLE_H && m.scrollHeight <= USABLE_H * 1.5) {
      let guard = 0;
      while (m.scrollHeight > USABLE_H + 2 && ff > 9 && guard++ < 80) { ff -= 0.3; m.style.fontSize = `${ff}px`; }
    }
    // Break at row/element boundaries so no table row is split across pages.
    const breaks = computeHtmlPageBreaks(m, USABLE_H);
    setFitFont(ff);
    setPageBreaks(breaks);
    setPage((p) => Math.min(p, breaks.length - 1));
  }, [html, baseFont, PAGE_H, PAGE_W, USABLE_H, landscape, paper, fontTick, bodyFont, M.left, M.right]);

  // Stacked pages: inject grey page-gap separators into the LIVE editor so the document
  // scrolls as A4 sheets. Skipped while editing (focus) so the caret is never disturbed —
  // re-runs on blur/content/size change.
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el || document.activeElement === el) return;
    el.style.fontSize = `${fitFont}px`;
    const n = injectPageGaps(el, { PH: PAGE_H, usableH: USABLE_H, gap: ADOC_SHEET_GAP, top: M.top, padL: M.left, padR: M.right });
    setSheetCount(n);
  }, [html, fitFont, USABLE_H, PAGE_H, M.top, M.bottom, M.left, M.right, fontTick, focused, pageBreaks]);

  const commit = () => {
    const el = ref.current;
    if (el && onChange) onChange(sanitize(el.innerHTML));
  };
  const exec = (cmd, val = null) => {
    if (!editable) return;
    ref.current?.focus();
    document.execCommand(cmd, false, val);
    commit();
  };
  const ALIGN_CMD = { left: 'justifyLeft', center: 'justifyCenter', right: 'justifyRight', justify: 'justifyFull' };

  // Re-focus the editor and restore the last selection — toolbar controls (font/size
  // menus) briefly move focus, and execCommand/range ops need the original selection back.
  const restoreRange = () => {
    const el = ref.current;
    const r = savedRange.current;
    if (!el) return;
    try {
      el.focus();
      if (r) { const sel = window.getSelection(); sel.removeAllRanges(); sel.addRange(r); }
    } catch { /* best-effort */ }
  };

  // Nearest block-level element inside the editor — used to apply a font/size to a whole
  // paragraph when nothing is selected (mirrors the block editor's per-block override).
  const blockAncestor = (node) => {
    const el = ref.current;
    let n = node && node.nodeType === 3 ? node.parentNode : node;
    while (n && n !== el) {
      if (n.nodeType === 1 && /^(P|DIV|LI|TD|TH|H1|H2|H3|H4|BLOCKQUOTE|CAPTION)$/.test(n.tagName)) return n;
      n = n.parentNode;
    }
    return null;
  };

  // Apply a font-family / font-size override. A text selection → wrap it in a tagged
  // <span class="adoc-font"> (sanitizeDocHtml keeps font on .adoc-font so it persists);
  // just a caret → set it on the containing block (whole-paragraph, block-editor parity).
  const applyFontStyle = (prop, value) => {
    if (!editable) return;
    restoreRange();
    const sel = typeof window !== 'undefined' ? window.getSelection() : null;
    const el = ref.current;
    if (!sel || sel.rangeCount === 0 || !el) return;
    const range = sel.getRangeAt(0);
    if (!el.contains(range.commonAncestorContainer)) return;
    if (sel.isCollapsed) {
      const blk = blockAncestor(range.startContainer);
      if (blk) { blk.classList.add('adoc-font'); if (value) blk.style[prop] = value; else blk.style.removeProperty(prop === 'fontFamily' ? 'font-family' : 'font-size'); }
    } else if (value) {
      try {
        const span = document.createElement('span');
        span.className = 'adoc-font';
        span.style[prop] = value;
        span.appendChild(range.extractContents());
        range.insertNode(span);
        const r = document.createRange();
        r.selectNodeContents(span);
        sel.removeAllRanges();
        sel.addRange(r);
        savedRange.current = r.cloneRange();
      } catch { /* exotic selections can throw on extract — ignore */ }
    }
    commit();
  };
  const setSelFont = (id) => applyFontStyle('fontFamily', docFontStack(id) || '');
  const setSelSize = (px) => applyFontStyle('fontSize', px ? `${px}px` : '');

  // ── Variable click-to-fill: click a yellow [Label] chip → inline input over it ──
  const openPhFill = (chip) => {
    if (!editable || !chip) return;
    const rect = chip.getBoundingClientRect();
    const sc = Math.max(0.4, Math.min(2, zoom || 1));
    setPhDraft('');
    setPhFill({
      top: rect.top, left: rect.left, height: rect.height, font: fitFont * sc,
      label: (chip.textContent || '').replace(/^\[|\]$/g, ''), chip,
    });
  };
  // Click a rendered formula → swap it for its raw `$tex$` text with the caret inside, so the TeX
  // can be edited in place. It re-renders on blur (see the editor's onBlur).
  const editMathSource = (mathEl) => {
    if (!editable || !mathEl) return;
    const tex = mathEl.getAttribute('data-tex') || '';
    const raw = mathEl.getAttribute('data-display') === '1' ? `$$${tex}$$` : `$${tex}$`;
    const tn = document.createTextNode(raw);
    mathEl.replaceWith(tn);
    const el = ref.current;
    if (!el) return;
    el.focus();
    try {
      const r = document.createRange();
      r.setStart(tn, Math.max(1, raw.length - (raw.startsWith('$$') ? 2 : 1))); // just before the closing $
      r.collapse(true);
      const sel = window.getSelection();
      sel.removeAllRanges();
      sel.addRange(r);
    } catch { /* caret placement is best-effort */ }
  };
  const commitPhFill = () => {
    const f = phFill;
    setPhFill(null);
    if (!f || !f.chip) { setPhDraft(''); return; }
    const val = phDraft.trim();
    if (val) {
      try { f.chip.replaceWith(document.createTextNode(val)); commit(); } catch { /* chip already gone */ }
    }
    setPhDraft('');
  };

  useImperativeHandle(apiRef, () => ({
    exec: (cmd) => exec(cmd),
    setAlign: (a) => exec(ALIGN_CMD[a] || 'justifyLeft'),
    setFont: (f) => setSelFont(f),
    resetMeta: () => {},
    getHtml: () => (ref.current ? sanitize(ref.current.innerHTML) : sanitize(html)),
    editingIdx: focused ? 0 : -1,
  }), [focused, editable, html]);

  useEffect(() => { onEditingChange?.(focused ? 0 : -1); }, [focused]);

  // Floating format bar over the current text selection (the inline toolbar, like the
  // block editor). Positioned by the selection's viewport rect so it works through the
  // paper's zoom transform. Shown only for a non-empty selection inside this editor.
  useEffect(() => {
    if (!editable) return undefined;
    const onSel = () => {
      const el = ref.current;
      const sel = typeof window !== 'undefined' ? window.getSelection() : null;
      if (!el || !sel || sel.rangeCount === 0) return;
      const range = sel.getRangeAt(0);
      if (!el.contains(range.commonAncestorContainer)) { setBar(null); setTableBar(null); activeCell.current = null; return; }
      savedRange.current = range.cloneRange(); // remember caret/selection for font+size
      // Live formatting state so the toolbar can highlight the active B/I/U + alignment.
      try {
        const q = (c) => { try { return document.queryCommandState(c); } catch { return false; } };
        setFmt({ bold: q('bold'), italic: q('italic'), underline: q('underline'), justifyLeft: q('justifyLeft'), justifyCenter: q('justifyCenter'), justifyRight: q('justifyRight') });
      } catch { /* queryCommandState unsupported */ }
      // Table toolbar — shown whenever the caret sits (collapsed) inside a table cell; a text
      // selection instead gets the formatting bar below.
      const startEl = range.startContainer.nodeType === 3 ? range.startContainer.parentElement : range.startContainer;
      const cell = startEl && startEl.closest ? startEl.closest('td,th') : null;
      activeCell.current = (cell && el.contains(cell)) ? cell : null;
      if (activeCell.current && sel.isCollapsed) {
        const t = (activeCell.current.closest('table') || activeCell.current).getBoundingClientRect();
        setTableBar({ top: Math.max(8, t.top - 44), left: Math.min(Math.max(150, t.left + t.width / 2), (window.innerWidth || 1200) - 150) });
      } else {
        setTableBar(null);
      }
      if (sel.isCollapsed) { setBar(null); return; }
      const rect = range.getBoundingClientRect();
      if (!rect || (!rect.width && !rect.height)) { setBar(null); return; }
      setBar({
        top: Math.max(8, rect.top - 46),
        left: Math.min(Math.max(140, rect.left + rect.width / 2), (window.innerWidth || 1200) - 140),
      });
    };
    document.addEventListener('selectionchange', onSel);
    return () => document.removeEventListener('selectionchange', onSel);
  }, [editable]);

  // ── Table editing — add/remove a row or column at the caret's cell. Buttons keep focus in
  // the editor (mousedown preventDefault), so commit() persists without the html effect
  // clobbering the live DOM; setFontTick forces the page-break re-measure. ─────────────────
  const mkCell = (tag) => { const c = document.createElement(tag === 'th' ? 'th' : 'td'); c.innerHTML = '<br/>'; return c; };
  const colIndexOf = (cell) => Array.prototype.indexOf.call(cell.parentElement.children, cell);
  const afterTableEdit = () => { commit(); setFontTick((t) => t + 1); };

  // ── Drag-to-resize table columns (right border) & rows (bottom border) ──────────
  // Hover a cell border → resize cursor; drag → set an explicit width/height that persists
  // (sanitizeDocHtml keeps width/style). Scale-aware so it works at any zoom.
  const resizeDrag = useRef(null);
  const RESIZE_EDGE = 6;
  const curScale = () => Math.max(0.4, Math.min(2, zoom || 1));
  // Cursor feedback + edge detection while NOT dragging.
  const onEditorHover = (e) => {
    if (resizeDrag.current) return;
    const el = ref.current; if (!el) return;
    const cell = e.target.closest && e.target.closest('td,th');
    if (!cell || !el.contains(cell)) { if (el.style.cursor) el.style.cursor = ''; return; }
    const r = cell.getBoundingClientRect();
    if (Math.abs(e.clientX - r.right) <= RESIZE_EDGE) el.style.cursor = 'col-resize';
    else if (Math.abs(e.clientY - r.bottom) <= RESIZE_EDGE) el.style.cursor = 'row-resize';
    else el.style.cursor = '';
  };
  // Returns true if a resize drag was started (so the caller skips its own click logic).
  const maybeStartResize = (e) => {
    if (!editable) return false;
    const el = ref.current; if (!el) return false;
    const cell = e.target.closest && e.target.closest('td,th');
    if (!cell || !el.contains(cell)) return false;
    const r = cell.getBoundingClientRect();
    const sc = curScale();
    const table = cell.closest('table');
    if (Math.abs(e.clientX - r.right) <= RESIZE_EDGE && table) {
      e.preventDefault();
      table.style.tableLayout = 'fixed'; // honour explicit column widths
      const idx = colIndexOf(cell);
      const colCells = Array.from(table.querySelectorAll('tr')).map((tr) => tr.children[idx]).filter(Boolean);
      resizeDrag.current = { type: 'col', startX: e.clientX, startW: r.width / sc, colCells, sc };
      return true;
    }
    if (Math.abs(e.clientY - r.bottom) <= RESIZE_EDGE) {
      e.preventDefault();
      const tr = cell.closest('tr');
      resizeDrag.current = { type: 'row', startY: e.clientY, startH: r.height / sc, tr, sc };
      return true;
    }
    return false;
  };
  useEffect(() => {
    const onMove = (e) => {
      const d = resizeDrag.current; if (!d) return;
      if (d.type === 'col') {
        const w = Math.max(24, Math.round(d.startW + (e.clientX - d.startX) / d.sc));
        d.colCells.forEach((c) => { c.style.width = `${w}px`; });
      } else {
        const h = Math.max(16, Math.round(d.startH + (e.clientY - d.startY) / d.sc));
        if (d.tr) d.tr.style.height = `${h}px`;
      }
    };
    const onUp = () => { if (resizeDrag.current) { resizeDrag.current = null; afterTableEdit(); } };
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
    return () => { document.removeEventListener('mousemove', onMove); document.removeEventListener('mouseup', onUp); };
  }, []);
  const tableAddRow = () => {
    const cell = activeCell.current; const tr = cell && cell.closest('tr'); if (!tr) return;
    const nr = document.createElement('tr');
    Array.from(tr.children).forEach((c) => {
      const nc = mkCell(c.tagName.toLowerCase());
      const cs = c.getAttribute('colspan'); if (cs) nc.setAttribute('colspan', cs);
      nr.appendChild(nc);
    });
    tr.parentNode.insertBefore(nr, tr.nextSibling);
    afterTableEdit();
  };
  const tableAddCol = () => {
    const cell = activeCell.current; const table = cell && cell.closest('table'); if (!table) return;
    const idx = colIndexOf(cell);
    table.querySelectorAll('tr').forEach((row) => {
      const refCell = row.children[idx] || null;
      const nc = mkCell(refCell && refCell.tagName.toLowerCase() === 'th' ? 'th' : 'td');
      row.insertBefore(nc, refCell ? refCell.nextSibling : null); // add to the RIGHT of the cell
    });
    afterTableEdit();
  };
  const tableDelRow = () => {
    const cell = activeCell.current; const tr = cell && cell.closest('tr'); const table = cell && cell.closest('table'); if (!tr || !table) return;
    if (table.querySelectorAll('tr').length <= 1) return; // keep at least one row
    tr.parentNode.removeChild(tr); activeCell.current = null; setTableBar(null); afterTableEdit();
  };
  const tableDelCol = () => {
    const cell = activeCell.current; const table = cell && cell.closest('table'); if (!table) return;
    const idx = colIndexOf(cell);
    const rows = table.querySelectorAll('tr');
    if (((rows[0] && rows[0].children.length) || 0) <= 1) return; // keep at least one column
    rows.forEach((row) => { const c = row.children[idx]; if (c) row.removeChild(c); });
    activeCell.current = null; setTableBar(null); afterTableEdit();
  };

  const scale = Math.max(0.4, Math.min(2, zoom || 1));
  const barBtn = 'w-8 h-8 rounded-lg flex items-center justify-center text-zinc-600 dark:text-zinc-300 hover:bg-zinc-100 dark:hover:bg-zinc-800 transition-colors';
  const navBtn = 'inline-flex items-center gap-1 px-3 py-1.5 rounded-lg border border-zinc-200 dark:border-zinc-700 disabled:opacity-40 hover:border-primary/40 transition-colors';
  return (
    <div className="w-full flex flex-col items-center gap-3">
      {/* Hidden measuring layer — full page width + the same padding as the sheet. */}
      <div
        aria-hidden="true"
        ref={measureRef}
        className="adoc-html"
        style={{ position: 'fixed', left: -99999, top: 0, width: PAGE_W, padding: `0 ${M.right}px 0 ${M.left}px`, boxSizing: 'border-box', fontFamily: bodyFont, lineHeight: 1.45, visibility: 'hidden', pointerEvents: 'none', zIndex: -1 }}
      />
      {/* Table toolbar — add/remove row & column at the caret cell (shown when not selecting). */}
      {tableBar && !bar && (
        <div
          style={{ position: 'fixed', top: tableBar.top, left: tableBar.left, transform: 'translateX(-50%)', zIndex: 60 }}
          className="flex items-center gap-1 rounded-xl border border-zinc-200 dark:border-zinc-700 bg-white dark:bg-zinc-900 px-1.5 py-1 shadow-xl"
          onMouseDown={(e) => e.preventDefault()}
        >
          <Tip label={isBn ? 'আনডু' : 'Undo'}><button type="button" onMouseDown={(e) => { e.preventDefault(); exec('undo'); }} className={barBtn}><IconArrowBackUp size={16} /></button></Tip>
          <Tip label={isBn ? 'রিডু' : 'Redo'}><button type="button" onMouseDown={(e) => { e.preventDefault(); exec('redo'); }} className={barBtn}><IconArrowForwardUp size={16} /></button></Tip>
          <span className="w-px h-5 bg-zinc-200 dark:bg-zinc-700 mx-0.5" />
          <Tip label={isBn ? 'সারি যোগ করুন' : 'Add row below'}><button type="button" onMouseDown={(e) => { e.preventDefault(); tableAddRow(); }} className={barBtn}><IconRowInsertBottom size={16} /></button></Tip>
          <Tip label={isBn ? 'কলাম যোগ করুন' : 'Add column right'}><button type="button" onMouseDown={(e) => { e.preventDefault(); tableAddCol(); }} className={barBtn}><IconColumnInsertRight size={16} /></button></Tip>
          <span className="w-px h-5 bg-zinc-200 dark:bg-zinc-700 mx-0.5" />
          <Tip label={isBn ? 'সারি মুছুন' : 'Delete row'}><button type="button" onMouseDown={(e) => { e.preventDefault(); tableDelRow(); }} className={`${barBtn} hover:text-red-500`}><IconRowRemove size={16} /></button></Tip>
          <Tip label={isBn ? 'কলাম মুছুন' : 'Delete column'}><button type="button" onMouseDown={(e) => { e.preventDefault(); tableDelCol(); }} className={`${barBtn} hover:text-red-500`}><IconColumnRemove size={16} /></button></Tip>
        </div>
      )}
      {bar && (
        <div
          style={{ position: 'fixed', top: bar.top, left: bar.left, transform: 'translateX(-50%)', zIndex: 60 }}
          className="flex items-center gap-1 rounded-xl border border-zinc-200 dark:border-zinc-700 bg-white dark:bg-zinc-900 px-1.5 py-1 shadow-xl"
          onMouseDown={(e) => e.preventDefault()}
        >
          {/* Font + size — applied to the current selection (block-editor parity) */}
          <FontSelect value="" onChange={(id) => setSelFont(id)} isBn={isBn} className="h-8 w-[118px] text-xs" />
          <CustomSelect
            value=""
            onChange={(v) => setSelSize(v)}
            options={[{ value: '', label: isBn ? 'সাইজ' : 'Size' }, ...SIZE_OPTS.map((n) => ({ value: n, label: isBn ? toBn(n) : String(n) }))]}
            className="h-8 w-[64px] text-xs"
          />
          <span className="w-px h-5 bg-zinc-200 dark:bg-zinc-700 mx-0.5" />
          {[['bold', IconBold, isBn ? 'বোল্ড' : 'Bold'], ['italic', IconItalic, isBn ? 'ইটালিক' : 'Italic'], ['underline', IconUnderline, isBn ? 'আন্ডারলাইন' : 'Underline']].map(([cmd, Icon, label]) => (
            <Tip key={cmd} label={label}>
              <button type="button" onMouseDown={(e) => { e.preventDefault(); exec(cmd); }} className={`${barBtn} ${fmt[cmd] ? 'bg-primary/15 text-primary' : ''}`}>
                <Icon size={15} />
              </button>
            </Tip>
          ))}
          <span className="w-px h-5 bg-zinc-200 dark:bg-zinc-700 mx-0.5" />
          {[['justifyLeft', IconAlignLeft, isBn ? 'বাঁয়ে' : 'Align left'], ['justifyCenter', IconAlignCenter, isBn ? 'মাঝে' : 'Align center'], ['justifyRight', IconAlignRight, isBn ? 'ডানে' : 'Align right']].map(([cmd, Icon, label]) => (
            <Tip key={cmd} label={label}>
              <button type="button" onMouseDown={(e) => { e.preventDefault(); exec(cmd); }} className={`${barBtn} ${fmt[cmd] ? 'bg-primary/15 text-primary' : ''}`}>
                <Icon size={15} />
              </button>
            </Tip>
          ))}
          <span className="w-px h-5 bg-zinc-200 dark:bg-zinc-700 mx-0.5" />
          {/* Undo / redo — native contentEditable history (covers both text and style edits). */}
          {[['undo', IconArrowBackUp, isBn ? 'আনডু' : 'Undo'], ['redo', IconArrowForwardUp, isBn ? 'রিডু' : 'Redo']].map(([cmd, Icon, label]) => (
            <Tip key={cmd} label={label}>
              <button type="button" onMouseDown={(e) => { e.preventDefault(); exec(cmd); }} className={barBtn}>
                <Icon size={15} />
              </button>
            </Tip>
          ))}
        </div>
      )}
      {/* Inline placeholder-fill input, positioned over the clicked yellow chip. */}
      {phFill && (
        <input
          autoFocus
          value={phDraft}
          placeholder={phFill.label}
          onChange={(e) => setPhDraft(e.target.value)}
          onBlur={commitPhFill}
          onKeyDown={(e) => {
            if (e.key === 'Enter') { e.preventDefault(); commitPhFill(); }
            else if (e.key === 'Escape') { e.preventDefault(); setPhFill(null); setPhDraft(''); }
          }}
          style={{
            position: 'fixed', top: phFill.top, left: phFill.left, zIndex: 65,
            height: Math.max(phFill.height, 18), fontSize: phFill.font, lineHeight: 1.2,
            minWidth: `${Math.max((phDraft || phFill.label || '').length + 2, 5)}ch`,
            padding: '0 3px', background: '#fff3cd', color: '#8a6d00',
            border: '1px solid #e6a700', borderRadius: 3, outline: 'none', fontFamily: bodyFont,
          }}
        />
      )}
      {/* Stacked A4 sheets — real separate pages: white sheets with shadow float on the
          dotted canvas, and the ONE editable flows over them with transparent gap-spacers
          (injectPageGaps) bridging each sheet break, so the doc scrolls page-by-page. */}
      <div style={{ width: PAGE_W * scale, height: (sheetCount * PAGE_H + (sheetCount - 1) * ADOC_SHEET_GAP) * scale, flexShrink: 0 }}>
        <div
          className="adoc-doc adoc-html"
          style={{
            position: 'relative',
            width: PAGE_W, height: sheetCount * PAGE_H + (sheetCount - 1) * ADOC_SHEET_GAP,
            transform: `scale(${scale})`, transformOrigin: 'top left',
            color: '#2c2c29', fontFamily: bodyFont,
          }}
        >
          <style>{`.adoc-html [contenteditable]:focus{outline:none;}${editable ? '.adoc-html .adoc-ph{cursor:pointer;}.adoc-html .adoc-ph:hover{outline:2px solid rgba(230,138,0,0.45);}.adoc-html .adoc-math{cursor:pointer;}.adoc-html .adoc-math:hover{outline:2px solid rgba(230,138,0,0.45);border-radius:3px;}' : ''}${docHtmlCss('.adoc-html')}`}</style>
          {/* One white sheet per page, behind the content. */}
          {Array.from({ length: sheetCount }).map((_, i) => (
            <div
              key={i}
              aria-hidden="true"
              style={{
                position: 'absolute', left: 0, top: i * (PAGE_H + ADOC_SHEET_GAP), width: PAGE_W, height: PAGE_H,
                background: '#fff', borderRadius: 4, border: '1px solid #ece9e4',
                boxShadow: '0 1px 2px rgba(0,0,0,0.06), 0 14px 40px rgba(0,0,0,0.10)', pointerEvents: 'none',
              }}
            />
          ))}
          <div
            ref={ref}
            contentEditable={editable}
            suppressContentEditableWarning
            onFocus={() => setFocused(true)}
            onBlur={() => {
              setFocused(false); commit(); setBar(null); setTableBar(null);
              // Re-render any formula that was opened for editing (deferred: the parent re-sets
              // the html on commit, then this re-applies the display decoration idempotently).
              requestAnimationFrame(() => { const el = ref.current; if (el && document.activeElement !== el) decorate(el); });
            }}
            onMouseMove={onEditorHover}
            onMouseDown={(e) => {
              if (!editable) return;
              if (maybeStartResize(e)) return; // drag a cell border → resize column/row
              const chip = e.target.closest && e.target.closest('.adoc-ph');
              if (chip) { e.preventDefault(); openPhFill(chip); return; } // click a [Label] → inline fill
              const math = e.target.closest && e.target.closest('.adoc-math');
              if (math) { e.preventDefault(); editMathSource(math); } // click a formula → edit its TeX
            }}
            style={{ position: 'absolute', left: 0, top: 0, width: PAGE_W, boxSizing: 'border-box', outline: 'none', background: 'transparent', padding: `${M.top}px ${M.right}px 0 ${M.left}px`, fontSize: fitFont, lineHeight: 1.45 }}
          />
        </div>
      </div>
    </div>
  );
});
